/**
 * Service de gestion des permissions de géolocalisation
 * Utilise l'API Permissions moderne avec fallback pour navigateurs anciens
 * En mode Capacitor, utilise directement le SDK natif
 */

/**
 * Détermine si on est en mode Capacitor
 */
function isCapacitorMode() {
  return window.isCapacitorApp === true
}

/**
 * États possibles des permissions de géolocalisation
 */
export const PERMISSION_STATES = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
  UNKNOWN: 'unknown',
}

/**
 * Informations sur le navigateur et la plateforme
 */
export function getBrowserInfo() {
  const userAgent = navigator.userAgent.toLowerCase()

  return {
    name: userAgent.includes('chrome')
      ? 'chrome'
      : userAgent.includes('safari')
        ? 'safari'
        : userAgent.includes('firefox')
          ? 'firefox'
          : 'unknown',
    platform: /mobile|android|iphone|ipad|ipod/.test(userAgent)
      ? 'mobile'
      : 'desktop',
    isIOS: /iphone|ipad|ipod/.test(userAgent),
    isAndroid: /android/.test(userAgent),
    isSafari: /safari/.test(userAgent) && !/chrome/.test(userAgent),
    isChrome: /chrome/.test(userAgent) && !/safari/.test(userAgent),
  }
}

/**
 * Vérifie si l'API Permissions est disponible
 */
function isPermissionsAPISupported() {
  return 'permissions' in navigator && 'query' in navigator.permissions
}

/**
 * Vérifie le statut de la permission de géolocalisation
 * @returns {Promise<string>} - 'granted', 'denied', 'prompt', ou 'unknown'
 */
export async function checkGeolocationPermission() {
  // En mode Capacitor, on utilise directement le SDK natif
  // Pas besoin de vérifier les permissions du navigateur
  if (isCapacitorMode()) {
    return PERMISSION_STATES.GRANTED
  }

  try {
    // Vérifier si la géolocalisation est supportée
    if (!navigator.geolocation) {
      return PERMISSION_STATES.UNKNOWN
    }

    // Utiliser l'API Permissions si disponible
    if (isPermissionsAPISupported()) {
      try {
        const permission = await navigator.permissions.query({
          name: 'geolocation',
        })
        return permission.state
      } catch (error) {
        console.warn(
          'API Permissions non supportée pour la géolocalisation:',
          error,
        )
      }
    }

    // Fallback: tenter d'obtenir la position pour déterminer le statut
    // Cette approche est moins précise mais fonctionne sur les navigateurs anciens
    try {
      await getCurrentPositionWithTimeout({
        enableHighAccuracy: false,
        timeout: 1000,
        maximumAge: 0,
      })
      return PERMISSION_STATES.GRANTED
    } catch (error) {
      if (error.message.includes('Permission')) {
        return PERMISSION_STATES.DENIED
      }
      // Pour les autres erreurs (timeout, unavailable), on considère que c'est 'prompt'
      return PERMISSION_STATES.PROMPT
    }
  } catch (error) {
    console.warn('Erreur lors de la vérification des permissions:', error)
    return PERMISSION_STATES.UNKNOWN
  }
}

/**
 * Options de géolocalisation optimisées par navigateur
 */
export function getBrowserSpecificOptions() {
  const browserInfo = getBrowserInfo()

  const baseOptions = {
    timeout: 10000,
    maximumAge: 300000, // 5 minutes
  }

  if (browserInfo.isIOS) {
    // iOS Safari : plus permissif avec le timeout, moins exigeant sur la précision
    return {
      ...baseOptions,
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 600000, // 10 minutes
    }
  }

  if (browserInfo.isAndroid && browserInfo.isChrome) {
    // Android Chrome : peut gérer une haute précision
    return {
      ...baseOptions,
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 180000, // 3 minutes
    }
  }

  if (browserInfo.isSafari && !browserInfo.isIOS) {
    // Safari desktop : modérément exigeant
    return {
      ...baseOptions,
      enableHighAccuracy: true,
      timeout: 12000,
    }
  }

  // Par défaut : haute précision
  return {
    ...baseOptions,
    enableHighAccuracy: true,
  }
}

/**
 * Récupère la position avec gestion des permissions
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<{lat:number, lng:number, accuracy?:number, source:string}>}
 */
export async function getCurrentPositionWithPermissionCheck(options = {}) {
  // En mode Capacitor, on utilise directement le SDK natif sans vérifier les permissions
  if (isCapacitorMode()) {
    const browserOptions = getBrowserSpecificOptions()
    const position = await getCurrentPositionWithFallback({
      ...browserOptions,
      ...options,
    })
    return {
      ...position,
      source: 'gps',
    }
  }

  // Vérifier le statut des permissions
  const permissionState = await checkGeolocationPermission()

  switch (permissionState) {
    case PERMISSION_STATES.DENIED:
      throw new Error(
        "Permission de localisation refusée. Veuillez l'activer dans les paramètres de votre navigateur.",
      )

    case PERMISSION_STATES.GRANTED:
      // Permission déjà accordée, utiliser les options optimisées
      const browserOptions = getBrowserSpecificOptions()
      const position = await getCurrentPositionWithFallback({
        ...browserOptions,
        ...options,
      })
      return {
        ...position,
        source: 'gps',
      }

    case PERMISSION_STATES.PROMPT:
    case PERMISSION_STATES.UNKNOWN:
    default:
      // La permission sera demandée par le navigateur
      try {
        const browserOptions2 = getBrowserSpecificOptions()
        const position2 = await getCurrentPositionWithFallback({
          ...browserOptions2,
          ...options,
        })
        return {
          ...position2,
          source: 'gps',
        }
      } catch (error) {
        // Si l'utilisateur refuse la permission native
        if (error.message.includes('Permission')) {
          throw new Error(
            "Permission de localisation refusée. Veuillez l'activer dans les paramètres de votre navigateur.",
          )
        }
        throw error
      }
  }
}

/**
 * Gestionnaire d'erreurs amélioré avec instructions spécifiques
 */
export function handlePermissionError(error, showNotification) {
  const browserInfo = getBrowserInfo()

  let title = 'Erreur de localisation'
  let body = "Impossible d'obtenir votre position actuelle"
  let type = 'error'
  let showSettingsHelp = false

  if (error.message.includes('Permission')) {
    title = 'Localisation requise'
    type = 'warning'
    showSettingsHelp = true

    // Instructions spécifiques par navigateur
    if (browserInfo.isIOS) {
      if (browserInfo.isSafari) {
        body =
          'Allez dans Réglages > Confidentialité > Services de localisation > Safari Websites > Autoriser'
      } else {
        body = 'Allez dans Réglages > [Navigateur] > Localisation > Autoriser'
      }
    } else if (browserInfo.isAndroid) {
      if (browserInfo.isChrome) {
        body =
          'Appuyez sur ⋮ > Paramètres > Paramètres du site > Localisation > Autoriser'
      } else {
        body =
          'Allez dans les paramètres de votre navigateur pour autoriser la localisation'
      }
    } else {
      // Desktop
      if (browserInfo.isChrome) {
        body =
          "Cliquez sur l'icône 🔒 dans la barre d'adresse, puis activez la Localisation"
      } else if (browserInfo.isSafari) {
        body =
          'Allez dans Safari > Préférences > Sites web > Localisation > Autoriser'
      } else if (browserInfo.isFirefox) {
        body =
          "Cliquez sur l'icône 🔒 dans la barre d'adresse, puis activez la Localisation"
      } else {
        body =
          "Autorisez l'accès à votre localisation dans les paramètres de votre navigateur"
      }
    }
  } else if (error.message.includes('Timeout')) {
    title = "Délai d'attente dépassé"
    body =
      'La localisation prend trop de temps. Essayez de sélectionner manuellement votre position.'
    type = 'warning'
  } else if (error.message.includes('unavailable')) {
    title = 'Localisation indisponible'
    body =
      "Le service de localisation n'est pas disponible. Utilisez la recherche manuelle."
    type = 'warning'
  }

  if (showNotification) {
    showNotification({
      title,
      body,
      type,
      duration: showSettingsHelp ? 6000 : 4000,
    })
  }

  return {
    title,
    body,
    type,
    showSettingsHelp,
    browserInfo,
  }
}

/**
 * Écoute les changements de permission (Chrome uniquement)
 * @param {Function} callback - Fonction appelée quand la permission change
 */
export function watchPermissionChanges(callback) {
  if (!isPermissionsAPISupported()) {
    return null
  }

  navigator.permissions
    .query({ name: 'geolocation' })
    .then(permission => {
      permission.addEventListener('change', () => {
        callback(permission.state)
      })
    })
    .catch(error => {
      console.warn("Impossible d'écouter les changements de permission:", error)
    })

  return null
}

// Import des fonctions existantes
import {
  getCurrentPositionWithFallback,
  getLocationWithTimeout,
} from './locationUtils.js'
